import {
    toActionBarBindingId,
    ACTION_BAR_HOTKEY_COUNT,
    formatDigitalBindingLabel,
    type DigitalInputBindingId,
    type InputBindings,
    type UiInputBindingId,
} from '@sorrem/shared';
import { useCallback, useEffect, useMemo, useRef, useSyncExternalStore } from 'react';
import { getPageInput } from '@sorrem-client/ui/input/page-input';

export interface InputBindingsState {
    bindings: InputBindings;
}

/** Every input binding, re-rendering when they change. */
export function useInputBindings(): InputBindingsState {
    const input = getPageInput();
    const bindings = useSyncExternalStore(
        useCallback((onChange: () => void) => input.subscribeBindings(onChange), [input]),
        () => input.getBindings(),
    );
    return { bindings };
}

/** Display label for a binding's first input, e.g. `1` or `Esc`; empty when unbound. */
export function formatBindingLabel(bindings: InputBindings, id: DigitalInputBindingId): string {
    const first = bindings[id][0];
    return first ? formatDigitalBindingLabel(first) : '';
}

/** The key on each action-bar slot, as it is shown (`1`, `D`), by slot; empty for an unbound slot. */
export function useActionBarKeyLabels(): readonly string[] {
    const { bindings } = useInputBindings();
    return useMemo(() => Array.from({ length: ACTION_BAR_HOTKEY_COUNT }, (_, index) => {
        const id = toActionBarBindingId(index);
        return id === undefined ? '' : formatBindingLabel(bindings, id);
    }), [bindings]);
}

/** A control's name with the key that works it, e.g. `Vessel (V)`; just the name when the key is unbound (empty). */
export function labelWithKey(label: string, keyLabel: string): string {
    return keyLabel === '' ? label : `${label} (${keyLabel})`;
}

/**
 * Calls `onPress` whenever the UI binding is pressed (by whatever input it
 * is bound to) while the game or nothing has focus. Pass `undefined` to
 * stop listening. For UI reactions only: gameplay bindings reach the game
 * directly and never go through React.
 */
export function useInputBindingPress(id: UiInputBindingId, onPress: (() => void) | undefined): void {
    const onPressRef = useRef(onPress);
    onPressRef.current = onPress;
    const isEnabled = onPress !== undefined;

    useEffect(() => {
        if (!isEnabled) {
            return undefined;
        }
        return getPageInput().onPress(id, () => onPressRef.current?.());
    }, [id, isEnabled]);
}

/**
 * Calls `onActivate` with the zero-based action-bar index whenever an
 * action-bar binding is pressed, and `onRelease` (if given) when it is let
 * go, for actions that must be held.
 */
export function useActionBarTrigger(
    onActivate: ((index: number) => void) | undefined,
    onRelease?: (index: number) => void,
): void {
    const onActivateRef = useRef(onActivate);
    onActivateRef.current = onActivate;
    const onReleaseRef = useRef(onRelease);
    onReleaseRef.current = onRelease;
    const isEnabled = onActivate !== undefined;

    useEffect(() => {
        if (!isEnabled) {
            return undefined;
        }
        const input = getPageInput();
        const unsubscribers = Array.from({ length: ACTION_BAR_HOTKEY_COUNT }, (_, index) => {
            const id = toActionBarBindingId(index);
            if (id === undefined) {
                return [];
            }
            return [
                input.onPress(id, () => onActivateRef.current?.(index)),
                input.onRelease(id, () => onReleaseRef.current?.(index)),
            ];
        }).flat();
        return (): void => {
            for (const unsubscribe of unsubscribers) {
                unsubscribe();
            }
        };
    }, [isEnabled]);
}
